import { cloneDeep, pick, toArray } from 'lodash';
import moment from 'moment';
import { CAMPAIGN } from '../constants/Define';
import { getMomentDate } from '../utils/commonUtils';
import BaseModel from './BaseModel';

const DEFAULT_CAMPAIGN_PLATFORMS = [
    CAMPAIGN.PLATFORMS.WEB_PC.value,
    CAMPAIGN.PLATFORMS.WEB_MOBILE.value,
];

export default class Campaign extends BaseModel {
    constructor(data = {}) {
        // Normalize API responses once; model copies must retain current form edits.
        const defaultPaymentTranslation = !data.__data__ && (data.translations || [])
            .find(translation => translation.isDefault)?.paymentCampaign;
        const campaignData = defaultPaymentTranslation ? {
            ...data,
            paymentCampaign: {
                ...data.paymentCampaign,
                ...pick(defaultPaymentTranslation, ['title', 'description', 'tooltip']),
            },
        } : data;
        super(campaignData);
        this.setData(campaignData)
    }

    setData(data = this.__data__, translatePaymentCampaign = false) {
        // Keep the current default-language form, including edits not saved yet.
        if (translatePaymentCampaign && !this.__paymentCampaignBeforeTranslate__) {
            this.__paymentCampaignBeforeTranslate__ = cloneDeep(this.paymentCampaign);
        }
        this.id = data.id;
        this.code = data.code || undefined;
        this.formatCode = data.formatCode || undefined;
        this.name = data.name || undefined;
        this.description = data.description || undefined;
        this.setProductType(data);
        this.displayProductType = data.productType || undefined;
        this.totalCount = data.totalCount || undefined;
        this.usedCount = data.usedCount || undefined;
        this.setSchedule(data);
        this.countries = data.countries || [];
        this.channel = data.channel || [];
        this.platforms = data.platforms || DEFAULT_CAMPAIGN_PLATFORMS;
        this.skuIds = data.skuIds || [];
        this.outletIds = (data.outletIds || data.outlets || []).map(item => item?.id || item);
        this.menuIds = (data.menuIds || data.menus || []).map(item => item?.id || item);
        this.menuItemIds = (data.menuItemIds || data.menuItems || []).map(item => ({
            id: item.id || item,
            code: item.code,
            name: item.name
        }));
        this.propertyIds = (data.propertyIds || data.properties || []).map(item => item?.id || item);
        this.hotel = data.hotel || undefined;
        this.minItemCount = data.minItemCount || undefined;
        this.maxItemCount = data.maxItemCount || undefined;
        this.minOrderAmount = data.minOrderAmount || undefined;
        this.totalOrderAmount = data.totalOrderAmount || undefined;
        this.codePrefix = data.codePrefix || undefined;
        this.codeCharCount = data.codeCharCount || 0;
        this.promotionType = data.promotionType || undefined;
        this.bonusType = data.bonusType || CAMPAIGN.BONUS_TYPE.DECREACE.id;
        this.maxUseCount = data.maxUseCount || undefined;
        this.maxCountPerUser = data.maxCountPerUser || undefined;
        this.userFrequencyLimitCount = data.userFrequencyLimitCount || undefined;
        this.userFrequencyLimitPeriod = data.userFrequencyLimitPeriod || CAMPAIGN.USER_FREQUENCY_LIMIT_PERIOD.NONE.id;
        this.maxRoomNightPerUser = data.maxRoomNightPerUser || undefined;
        this.discountType = data.discountType || CAMPAIGN.DISCOUNT_TYPE.AMOUNT.id;
        this.discountValue = (data.discountValue === '0' || data.discountValue === 0) ? 0 : data.discountValue || undefined;
        this.maxDiscountValue = data.maxDiscountValue || undefined;
        this.timezoneOffset = data.timezoneOffset || undefined;
        this.setOptionSku(data);
        this.maxCount = data.maxCount || 0;
        this.minRoomNumber = data?.hotel?.minRoomNumber || 0;
        this.maxRoomNumber = data?.hotel?.maxRoomNumber || 0;
        this.minLenghOfStay = data?.hotel?.minLenghOfStay || 0;
        this.maxLenghOfStay = data?.hotel?.maxLenghOfStay || 0;
        this.totalRateConfiged = data?.hotel?.totalRateConfiged || 0;
        this.startTime = data.startTime || undefined;
        this.endTime = data.endTime || undefined;
        this.canDelete = data.canDelete || false;
        // this.objectType = data.objectType || CAMPAIGN.OBJECT_TYPE.ALL.id;
        this.setObjectType(data)
        this.memberShipTypes = data.memberShipTypes || [];
        this.codeType = data.codeType || CAMPAIGN.CODE_TYPE.STANDARD.id;
        this.emails = data.emails || [];
        this.emailSuffix = data.emailSuffix || [];
        this.freeNightStay = data.freeNightStay || 0;
        this.loyaltyCode = data.loyaltyCode;
        this.canEdit = data.canEdit || true;
        this.maxCodeCount = data.maxCodeCount || 0;
        // this.loyaltyBenefitCode = data.loyaltyBenefitCode || undefined;
        // this.loyaltyCampaignCode = data.loyaltyCampaignCode || undefined;
        this.isLoyaltyVoucher = data.isLoyaltyVoucher || false;
        this.objectTypeDataEmail = data.objectTypeDataEmail || data.objectType || undefined;
        this.isLoyaltyBenefit = data.isLoyaltyBenefit || false;
        this.discountMode = data.discountMode || 'ITEM';
        this.promotionMarkup = new PromotionMarkup(data.promotionMarkup);
        this.isPaymentCampaign = data.isPaymentCampaign || false;
        this.paymentCampaign = new PaymentCampaign(translatePaymentCampaign
            ? this.mergePaymentCampaign(this.__paymentCampaignBeforeTranslate__ || data.paymentCampaign, this.translate.paymentCampaign)
            : this.__paymentCampaignBeforeTranslate__ || data.paymentCampaign || this.mergePaymentCampaign(this.paymentCampaign, this.translate.paymentCampaign));
        if (!translatePaymentCampaign) {
            delete this.__paymentCampaignBeforeTranslate__;
        }
        this.languageCode = translatePaymentCampaign
            ? this.languageCode
            : data.languageCode || this.languageCode || undefined;
        
        this.IsExtendPromotion = data.IsExtendPromotion
        this.PromotionExtended = data.PromotionExtended
        this.ExPromotionMemberShipTypes = data.ExPromotionMemberShipTypes
        this.ExEmailSuffix = data.ExEmailSuffix
        this.ExPromotionEmails = data.ExPromotionEmails
        this.ExObjectType = data.ExObjectType
        this.tagId = data.tagId || undefined
        this.isActive= data.isActive
        this.isCampaignPoint = data.isCampaignPoint
        this.productValues = data.productValues
        this.isCampaignStaynFun = data.isCampaignStaynFun || false
    }

    mergePaymentCampaign(paymentCampaign = {}, translation = {}) {
        return {
            ...paymentCampaign,
            title: translation?.title ?? null,
            tooltip: translation?.tooltip ?? null,
            description: translation?.description ?? null,
        };
    }

    setObjectType(data = {}) {
        this.objectType = (data.objectType && (data.objectType === 'SPECIFIC' || data.objectType === 'EMAIL_SUFFIX') ? CAMPAIGN.OBJECT_TYPE.ANOTHER.id : data.objectType) || CAMPAIGN.OBJECT_TYPE.ALL.id;
        this.isLoyaltyBenefit = data.isLoyaltyBenefit || false;
    }

    setOptionSku(data = {}) {
        this.optionSku = (data.skuIds || []).length || (data.menuItemIds || []).length || (data.productType || []).includes(CAMPAIGN.PRODUCT_TYPE.FNB.value)
            ? CAMPAIGN.OPTION_SKU.SPECIFIC.id
            : CAMPAIGN.OPTION_SKU.ALL.id;
    }

    getProductType() {
        if ((this.productType || []).indexOf(CAMPAIGN.PRODUCT_TYPE.SKU.value) >= 0) {
            return [CAMPAIGN.PRODUCT_TYPE.SKU.value]
        }
        return this.productType
    }

    hasUserLimitConfig() {
        return this.objectType === CAMPAIGN.OBJECT_TYPE.MEMBER.id
            || this.objectType === CAMPAIGN.OBJECT_TYPE.ANOTHER.id;
    }

    isHotelCampaign() {
        return (this.productType || []).some(
            item => `${item}`.toUpperCase() === CAMPAIGN.PRODUCT_TYPE.HOTEL.value
        );
    }

    getUserFrequencyLimitCount() {
        if (!this.hasUserLimitConfig()) return null;
        const value = Number(this.userFrequencyLimitCount);
        return this.userFrequencyLimitCount && Number.isFinite(value) ? value : null;
    }

    getUserFrequencyLimitPeriod() {
        return this.getUserFrequencyLimitCount() ? this.userFrequencyLimitPeriod : null;
    }

    getMaxRoomNightPerUser() {
        if (!this.hasUserLimitConfig() || !this.isHotelCampaign()) return null;
        const value = Number(this.maxRoomNightPerUser);
        return this.maxRoomNightPerUser && Number.isFinite(value) ? value : null;
    }

    getObjectTypeEmail() {
        if(this.objectType === CAMPAIGN.OBJECT_TYPE.ANOTHER.id) {
            return this.objectTypeDataEmail
        }
        return this.objectType
    }

    addSchedules({
        startDate, endDate,
        scheduleType = CAMPAIGN.SCHADULE_TYPE.BOOKING_DATE.id,
        ...restParams
    }) {
        this.schedules.push(new CampaignSchedule({
            startDate, endDate,
            scheduleType,
            ...restParams
        }))
    }

    setSchedule(data = {}) {
        this.schedules = (data.schedules || []).map(item => new CampaignSchedule(item));
        if (!this.schedules?.length) {
            this.schedules = [new CampaignSchedule()]
        }
    }

    setProductType(data = {}) {
        if ((data.productType || []).indexOf(CAMPAIGN.PRODUCT_TYPE.SKU.value) >= 0) {
            this.productType = toArray(CAMPAIGN.PRODUCT_TYPE)
                .filter(item => item.id !== CAMPAIGN.PRODUCT_TYPE.HOTEL.id && item.id !== CAMPAIGN.PRODUCT_TYPE.FNB.id)
                .map(item => item.value)
        } else {
            this.productType = data.productType || []
        }
        this.maxCount = data.maxCount || 0;
    }

    export(hasTranslate = false) {
        let result = super.export({
            id: this.id,
            name: this.name,
            description: this.description,
            schedules: this.schedules?.length && this.schedules.map(item => item.export()),
            productType: this.getProductType(),
            countries: this.countries,
            channel: this.channel,
            platforms: this.platforms || DEFAULT_CAMPAIGN_PLATFORMS,
            skuIds: this.optionSku === CAMPAIGN.OPTION_SKU.ALL.id ? [] : this.skuIds,
            outletIds: this.outletIds,
            menuIds: this.menuIds,
            menuItemIds: this.menuItemIds,
            propertyIds: this.propertyIds,
            hotel: this.hotel,
            minItemCount: this.minItemCount,
            maxItemCount: this.maxItemCount,
            minOrderAmount: this.minOrderAmount,
            totalOrderAmount: this.totalOrderAmount,
            codePrefix: this.codePrefix,
            codeCharCount: this.codeCharCount || 0,
            promotionType: this.promotionType,
            maxUseCount: this.maxUseCount,
            maxCountPerUser: this.maxCountPerUser,
            userFrequencyLimitCount: this.getUserFrequencyLimitCount(),
            userFrequencyLimitPeriod: this.getUserFrequencyLimitPeriod(),
            maxRoomNightPerUser: this.getMaxRoomNightPerUser(),
            discountType: this.discountType,
            discountValue: this.discountValue,
            maxDiscountValue: this.maxDiscountValue,
            timezoneOffset: this.timezoneOffset,
            // objectType: this.objectType,
            objectType: this.getObjectTypeEmail(),
            memberShipTypes: this.memberShipTypes,
            codeType: this.codeType,
            emails: this.emails,
            emailSuffix: this.emailSuffix,
            freeNightStay: this.freeNightStay || 0,
            loyaltyCode: this.loyaltyCode,
            maxCodeCount: this.maxCodeCount || 0,
            // loyaltyBenefitCode: this.loyaltyBenefitCode,
            // loyaltyCampaignCode: this.loyaltyCampaignCode,
            isLoyaltyVoucher: this.isLoyaltyVoucher || false,
            isLoyaltyBenefit: this.isLoyaltyBenefit || false,
            discountMode: this.discountMode || 'ITEM',
            promotionMarkup: this.promotionMarkup || {},
            isPaymentCampaign : this.isPaymentCampaign || false,
            paymentCampaign : this.paymentCampaign && this.paymentCampaign.export(),
            IsExtendPromotion: this.IsExtendPromotion,
            tagId: this.tagId || null,
            isActive: this.isActive,
            isCampaignPoint: this.isCampaignPoint,
            productValues: this.productValues,
            isCampaignStaynFun: this.isCampaignStaynFun || false,
        });
        if (hasTranslate) result.translate = () => {
            return {
                languageCode: this.languageCode || 'vi-VN',
                paymentCampaign : this.paymentCampaign && this.paymentCampaign.export(),
                campaignId: this.id,
                name: this.name,
                description: this.description
            }
        }
        if (this.IsExtendPromotion) {
          let exPromotion = {
            PromotionExtended: this.PromotionExtended,
            ExPromotionMemberShipTypes: this.ExPromotionMemberShipTypes,
          }
          if (this.ExPromotionMemberShipTypes && this.ExPromotionMemberShipTypes.length) {
            exPromotion = { ...exPromotion, ExPromotionMemberShipTypes: this.ExPromotionMemberShipTypes}
          }
          if (this.ExEmailSuffix && this.ExEmailSuffix.length) {
            exPromotion = { ...exPromotion, ExEmailSuffix: this.ExEmailSuffix, ExObjectType: this.ExObjectType}
          }
          if (this.ExPromotionEmails && this.ExPromotionEmails.length) {
            exPromotion = { ...exPromotion, ExPromotionEmails: this.ExPromotionEmails, ExObjectType: this.ExObjectType}
          }
          return {
            ...result,
            ...exPromotion
          }
        }
        return result;
    }
    setTranslate(languageCode = undefined, getDefaultOnEmpty = false) {
      super.setTranslate(languageCode, getDefaultOnEmpty)
      if (!this.translate || Object.keys(this.translate).length === 0) {
        this.translate = {
          isDefault: false,
          languageCode: languageCode,
          languageName: "",
          name: "",
          paymentCampaign: undefined,
          promotionExtended: {
            avatarUrl: "",
            campaignCode: "",
            ctaName: "",
            detailDescription: "",
            labelWording: "",
            languageCode: languageCode,
            name: "",
            shortDescription: "",
            thumbnailUrl: "",
          },
        }
      }
  }
}

export class CampaignSchedule {
    constructor(data = {}) {
        this.setData(data)
    }

    setData(data = {}) {
        this.id = data.id;
        this.startDate = moment(data.startDate);
        this.endDate = moment(data.endDate);
        this.scheduleType = data.scheduleType || CAMPAIGN.SCHADULE_TYPE.BOOKING_DATE.id
    }

    export() {
        return {
            id: this.id,
            startDate: this.startDate && this.startDate.utcOffset(0, true).format(),
            endDate: this.endDate && this.endDate.utcOffset(0, true).format(),
            scheduleType: this.scheduleType,
        }
    }
}

export class PaymentCampaign {
    constructor(data = {}) {
        this.setData(data)
    }

    parseNumber(value, defaultValue = 0) {
        if (value === null || value === undefined || value === '') {
            return defaultValue;
        }
        const number = Number(`${value}`.replace(/,/g, ''));
        return Number.isNaN(number) ? defaultValue : number;
    }

    setData(data = {}) {
        this.refCode = data.refCode;
        this.bankType = data.bankType || CAMPAIGN.PAYMENT_BANK_TYPE.ONEPAY.id;
        this.bankCode = data.bankCode;
        this.bankGateway = data.bankGateway;
        this.bankTitle = data.bankTitle;
        this.bankLogo = data.bankLogo;
        this.bankPaymentMethod = data.bankPaymentMethod;
        this.bankDiscountType = data.bankDiscountType;
        this.bankDiscountValue = data.bankDiscountValue ?? 0;
        this.bankContributionPercent = data.bankContributionPercent ?? null;
        const contributionType = toArray(CAMPAIGN.CONTRIBUTION_TYPE)
            .find(type => type.value === data.contributionType || type.id === data.contributionType);
        this.contributionType = contributionType
            ? contributionType.id
            : CAMPAIGN.CONTRIBUTION_TYPE.VPT.id;
        this.bankMaxDiscountValue = this.parseNumber(data.bankMaxDiscountValue);
        this.minOrderValue = data.minOrderValue ?? 0;
        this.totalBudget = data.totalBudget ?? null;
        this.budgetUsed = data.budgetUsed ?? 0;
        this.applyForPaymentMethods = this.parseList(data.applyForPaymentMethods);
        this.allowMultiPromotion = data.allowMultiPromotion ?? false;
        this.title = data.title ?? null;
        this.description = data.description ?? null;
        this.tooltip = data.tooltip ?? null;
        this.applyForSupplierCodes = this.parseSupplierCodes(data.applyForSupplierCodes);
    }

    parseList(value) {
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string' && value) {
            return value.split(',').map(item => item.trim()).filter(item => item);
        }
        return [];
    }

    parseSupplierCodes(applyForSupplierCodes) {
        if (Array.isArray(applyForSupplierCodes)) {
            return applyForSupplierCodes;
        }
        if (typeof applyForSupplierCodes === 'string' && applyForSupplierCodes) {
            return applyForSupplierCodes.split(',').map(item => item.trim()).filter(item => item);
        }
        return [];
    }

    getApplyForSupplierCodes() {
        if (Array.isArray(this.applyForSupplierCodes)) {
            return this.applyForSupplierCodes.length ? this.applyForSupplierCodes.join(',') : undefined;
        }
        return this.applyForSupplierCodes || undefined;
    }

    export(hasTranslate = false) {
        const result = {
            bankType: this.bankType || CAMPAIGN.PAYMENT_BANK_TYPE.ONEPAY.id,
            bankCode: this.bankCode,
            bankTitle: this.bankTitle,
            bankLogo: this.bankLogo,
            bankDiscountType: this.bankDiscountType,
            bankDiscountValue: this.bankDiscountValue ?? 0,
            bankContributionPercent: this.bankContributionPercent === null
                ? null
                : this.parseNumber(this.bankContributionPercent),
            contributionType: this.contributionType || CAMPAIGN.CONTRIBUTION_TYPE.VPT.id,
            totalBudget: this.totalBudget,
            title: this.title || null,
            tooltip: this.tooltip || null,
            description: this.description || null
        };
        if (result.bankType === CAMPAIGN.PAYMENT_BANK_TYPE.VPT.id) {
            result.bankMaxDiscountValue = this.bankDiscountType === CAMPAIGN.BANK_DISCOUNT_TYPE.PERCENT.id
                ? this.bankMaxDiscountValue
                : 0;
            result.minOrderValue = this.minOrderValue ?? 0;
            result.applyForPaymentMethods = this.applyForPaymentMethods || [];
        } else {
            result.refCode = this.refCode;
            result.bankGateway = this.bankGateway;
            result.bankPaymentMethod = this.bankPaymentMethod;
        }
        result.applyForSupplierCodes = this.getApplyForSupplierCodes();
        result.allowMultiPromotion = this.allowMultiPromotion ?? false;
        if (hasTranslate) result.translate = () => {
            return {
                title: this.title,
                tooltip: this.tooltip,
                description: this.description,
                languageCode: this.languageCode || 'vi-VN'
            }
        }
        return result;
    }
}

export class MarkupCampaign {
    constructor(data = {}) {
        this.setData(data)
    }

    setData(data = {}) {
        this.markupConfigId = data.markupConfigId;
        this.markupConfigName = data.markupConfigName;
        this.hotelConfigs = data.hotelConfigs;
    }

    export() {
        return {
            markupConfigId: this.markupConfigId,
            markupConfigName: this.markupConfigName,
            hotelConfigs: this.hotelConfigs
        }
    }
}

export class PromotionMarkup {
    constructor(data = {}) {
        this.setData(data)
    }

    setData(data = {}) {
        this.markupCampaign(data);
        this.airlineCodes = data ? (data.airlineCodes || undefined) : undefined;
        this.isMultipleRoomNight = data ? (data.isMultipleRoomNight || false) : undefined;
        this.minRoomNight = data ? (data.minRoomNight || undefined) : undefined;
        this.maxRoomNight = data ? (data.maxRoomNight || undefined) : undefined;
    }

    markupCampaign(data = {}){
        this.markupCampaigns = data ? (data.markupCampaigns || []).map(item => new MarkupCampaign(item)) : [];
    }

    export() {
        return {
            markupCampaigns: this.markupCampaigns,
            airlineCodes: this.airlineCodes,
            isMultipleRoomNight: this.isMultipleRoomNight,
            minRoomNight: this.minRoomNight,
            maxRoomNight: this.maxRoomNight
        }
    }
}
